import { useAppSelector } from '@redux/hooks';
import { Link } from '@tkww/orion-web-ds-action';
import { GridColumn } from '@tkww/orion-web-ds-structure';
import { GridRow } from '@tkww/orion-web-ds-structure';
import React, { type FC, useCallback, useMemo } from 'react';
import type { Decorated } from 'types/vendor';

import useAnalyticsContext from '@hooks/useAnalyticsContext';
import AnalyticsConstants from '../../../../../../../constants/analytics';

import type { Designers as DesignersType } from '@typings/vendor';
import VendorAnalytics from '../../../../../../decorators/vendorAnalytics';
import ParentStyles from '../styles.scss';
import Styles from './styles.scss';

const dressTheme = (theme: string) => {
	const dressTypes = [
		'wedding-dresses',
		'flower-girl-dresses',
		'bridesmaid-dress',
		'mother-of-the-bride-dresses',
	];
	return dressTypes.indexOf(theme) !== -1;
};

const jewelryTheme = (theme: string) => {
	const jewelryTypes = [
		'engagement-rings',
		'wedding-rings',
		'tuxedos',
		'wedding-accessories',
		'wedding-jewelry',
	];
	return jewelryTypes.indexOf(theme) !== -1;
};

interface LinksProps {
	designers: Decorated['designers'];
}

const Links = ({ designers }: LinksProps) => {
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const { track } = useAnalyticsContext();
	const vendorAnalytics = useMemo(() => new VendorAnalytics(vendor), [vendor]);

	const designerClick = useCallback(
		(designer: DesignersType) => {
			track({
				event: AnalyticsConstants.CLICK_THROUGH_TO_DESIGNER,
				properties: {
					designerId: designer.odbId,
					designerName: designer.name,
					designerURL: designer.url,
					...vendorAnalytics.baseEventProps(),
				},
			});
		},
		[vendorAnalytics, track],
	);

	return (
		<>
			{designers?.map((designer) => (
				<div className={Styles.linkWrapper} key={designer.odbId}>
					<Link
						styleType="primary"
						size="md"
						href={designer.url}
						className={Styles.designerLink}
						onClick={() => designerClick(designer)}
					>
						{designer.name}
					</Link>
				</div>
			))}
		</>
	);
};

interface Props {
	designers: Decorated['designers'];
	columnSize?: '6' | '12';
}

export const Designers: FC<Props> = (props) => {
	const { designers, columnSize = '6' } = props;
	const designerType = designers?.[0]?.theme;

	const collectionLabel = useMemo(() => {
		if (designerType && dressTheme(designerType)) {
			return 'Gown Collections';
		}
		if (designerType && jewelryTheme(designerType)) {
			return 'Jewelry Collections';
		}

		return false;
	}, [designerType]);

	if (!designers) {
		return null;
	}

	return (
		<GridRow className={ParentStyles.detailSection}>
			<GridColumn xs="12" md={columnSize}>
				<div className={Styles.collectionLabel}>{collectionLabel}</div>
				<div>
					<Links designers={designers} />
				</div>
			</GridColumn>
		</GridRow>
	);
};
